import classnames from 'classnames'
import { FieldValidator, useField } from 'formik'
import React, { ComponentPropsWithoutRef, useId, useMemo } from 'react'

import styles from './Checkbox.module.scss'

type CheckboxProps = React.PropsWithChildren<{
  type?: 'checkbox' | 'radio'
  label?: string
  omitErrorDisplay?: boolean
  toggle?: boolean
  plain?: boolean
  touched?: boolean
  error?: string
}> &
  Omit<React.ComponentPropsWithoutRef<'input'>, 'type'>

const Checkbox = React.memo(
  React.forwardRef(function Checkbox(
    { type, label, omitErrorDisplay, toggle, plain, touched, error, children, ...componentProps }: CheckboxProps,
    ref: React.ForwardedRef<HTMLInputElement>
  ) {
    if (plain) {
      return (
        <input
          aria-label={label}
          ref={ref}
          type={type || 'checkbox'}
          {...componentProps}
          className={styles.plainCheckbox}
        />
      )
    }

    const id = useId()

    const visibleError = (touched && error) || null
    return (
      <div
        className={classnames(styles.Checkbox, {
          [styles.standard]: !toggle,
          [styles.toggle]: toggle,
          [styles.hasError]: Boolean(visibleError),
          [styles.isDisabled]: componentProps.disabled,
        })}
      >
        <div className={styles.wrapper}>
          <input id={id} aria-label={label} ref={ref} type={type || 'checkbox'} {...componentProps} />
          <label htmlFor={id} aria-hidden>
            <span className={styles.checkbox}></span>
            {label && <span aria-hidden>{label}</span>}
            {children}
          </label>
        </div>
        {!omitErrorDisplay && <div className={styles.errorMessage}>{visibleError}</div>}
      </div>
    )
  })
)

export default Checkbox

type CheckboxFormikProps = {
  name: string
  validate?: FieldValidator
} & ComponentPropsWithoutRef<typeof Checkbox>

export const CheckboxFormik = React.memo(function CheckboxFormik({
  type,
  name,
  validate,
  ...componentProps
}: CheckboxFormikProps) {
  const inputRef = React.useRef<HTMLInputElement>(null)

  // This is not a useCallback on purpose to force the field to re-register
  // on every render.
  // That is necessary because otherwise Formik may forget a group of checkboxes
  // when the last checkbox is removed dynamically.
  // 1. 5 checkboxes with the same name are rendered and register themselves.
  // 2. Because they all have the same name, only the last one ends up in
  //    Formik's fieldRegistry.
  // 3. The 5th checkbox is removed and unregisters itself from Formik, so the
  //    field is no longer in the fieldRegistry.
  // 4. The other 4 checkboxes never re-register themselves because they already
  //    did so when they were first rendered.
  const doValidate = (value?: any) => {
    if (!inputRef.current?.validity.valid) {
      return inputRef.current?.validationMessage
    }
    if (validate) {
      return validate(value)
    }
  }

  const [field, meta, _helpers] = useField({
    name,
    validate: doValidate,
    type: type || 'checkbox',
    ...componentProps,
  })

  // Wrap onChange so we call both Formik's change handler as well as one passed in through props.
  const onChange = useMemo<React.ChangeEventHandler<HTMLInputElement>>(
    () =>
      componentProps.onChange
        ? (e) => {
            field.onChange(e)
            componentProps.onChange && componentProps.onChange(e)
          }
        : field.onChange,
    [componentProps.onChange, field.onChange]
  )

  return (
    <Checkbox
      ref={inputRef}
      touched={meta.touched}
      error={meta.error}
      {...componentProps}
      type={type}
      {...field}
      onChange={onChange}
    />
  )
})
